"use client";

import type { Key } from "react";
import type { LocationItem } from "./types";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GOOGLE_MAP_SCRIPT_BASE_URL } from "react-google-autocomplete/lib/constants";
import { loadGoogleMapScript } from "react-google-autocomplete/lib/utils";
import { useLocale } from "~www/components/LocaleProvider";
import { getGoogleMapsApiKey } from "~www/lib/google-maps/api-key";
import { getMarkerIcon } from "~www/lib/google-maps/map-pin";
import { syncMarkersToMap } from "./sync-markers-to-map";

interface LocationsMapProps {
  locations: LocationItem[];
  selectedLocationId: Key | null;
  onSelectLocation: (id: Key) => void;
}

/** Pad added around a single marker so fitBounds doesn't zoom to max level. */
const SINGLE_MARKER_BOUNDS_PAD = 0.01;

function buildDefaultMapOptions(
  geographicCenter: { lat: number; lng: number },
  defaultMapZoom: number,
): google.maps.MapOptions {
  return {
    center: geographicCenter,
    disableDefaultUI: true,
    gestureHandling: "greedy",
    zoom: defaultMapZoom,
    zoomControl: true,
  };
}

export function LocationsMap({
  locations,
  selectedLocationId,
  onSelectLocation,
}: LocationsMapProps) {
  const locale = useLocale();
  const { defaultMapZoom, geographicCenter } = locale.metadata.country;
  const defaultMapOptions = useMemo(
    () => buildDefaultMapOptions(geographicCenter, defaultMapZoom),
    [defaultMapZoom, geographicCenter],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<Key, google.maps.Marker>>(new Map());

  const [isReady, setIsReady] = useState(false);

  const apiKey = getGoogleMapsApiKey();

  useEffect(() => {
    if (!apiKey || !containerRef.current) {
      return;
    }
    let cancelled = false;

    const scriptUrl = `${GOOGLE_MAP_SCRIPT_BASE_URL}?key=${apiKey}`;
    loadGoogleMapScript(GOOGLE_MAP_SCRIPT_BASE_URL, scriptUrl)
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }

        mapRef.current = new google.maps.Map(
          containerRef.current,
          defaultMapOptions,
        );

        setIsReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Google Maps failed to load:", err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, defaultMapOptions]);

  const fitMarkerBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || markersRef.current.size === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    for (const marker of markersRef.current.values()) {
      const pos = marker.getPosition();
      if (pos) {
        bounds.extend(pos);
      }
    }

    if (markersRef.current.size === 1) {
      const ne = bounds.getNorthEast();
      bounds.extend({
        lat: ne.lat() + SINGLE_MARKER_BOUNDS_PAD,
        lng: ne.lng() + SINGLE_MARKER_BOUNDS_PAD,
      });
      bounds.extend({
        lat: ne.lat() - SINGLE_MARKER_BOUNDS_PAD,
        lng: ne.lng() - SINGLE_MARKER_BOUNDS_PAD,
      });
    }

    map.fitBounds(bounds);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!isReady || !map) {
      return;
    }

    syncMarkersToMap(
      map,
      markersRef.current,
      locations,
      selectedLocationId,
      onSelectLocation,
    );
    fitMarkerBounds();
    // Intentionally omitting selectedLocationId — icon updates happen in the next effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, locations, onSelectLocation, fitMarkerBounds]);

  // Separate effect for icon updates so a selection change doesn't tear down and
  // recreate all markers — only the pin appearance and pan position update.
  useEffect(() => {
    if (!isReady) {
      return;
    }

    for (const [id, marker] of markersRef.current) {
      marker.setIcon(getMarkerIcon(id === selectedLocationId));
    }

    if (selectedLocationId != null) {
      const marker = markersRef.current.get(selectedLocationId);
      const pos = marker?.getPosition();
      if (pos) {
        mapRef.current?.panTo(pos);
      }
    }
  }, [isReady, selectedLocationId]);

  if (!apiKey) {
    return null;
  }

  return (
    <div
      className="locations-map h-full w-full overflow-hidden rounded-2xl"
      ref={containerRef}
    />
  );
}
